import React from 'react';
import { Dialog, useMediaQuery } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RobotFace from './RobotFace';
import { useT } from '../../../i18n';

/** Bump to show the modal again to everybody, e.g. after a new round of features. */
export const ASSISTANT_INTRO_VERSION = '2026.11';
const STORAGE_KEY = `assistantIntroSeen_${ASSISTANT_INTRO_VERSION}`;

export const introAlreadySeen = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    // Without storage, do not nag on every page load.
    return true;
  }
};

const markIntroSeen = (): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch (e) {
    // The modal may show again next visit; harmless.
  }
};

const useStyles = makeStyles((theme) => ({
  // A column with the list as the only part that scrolls: on a phone the
  // features do not fit, and the buttons must stay reachable.
  paper: {
    borderRadius: '22px',
    maxWidth: '520px',
    width: '100%',
    margin: '16px',
    maxHeight: 'calc(100% - 32px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.down(520)]: {
      margin: '12px',
      width: 'calc(100% - 24px)',
      maxHeight: 'calc(100% - 24px)',
      borderRadius: '18px',
    },
  },
  hero: {
    padding: '26px 24px 18px',
    background: 'linear-gradient(135deg, #0B64D1 0%, #6C4BF4 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
    [theme.breakpoints.down(520)]: { padding: '18px 16px 14px', gap: '12px', alignItems: 'flex-start' },
    // A phone on its side: the header must not eat the list.
    '@media (max-height: 480px)': { padding: '12px 20px 10px' },
  },
  heroRobot: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    [theme.breakpoints.down(520)]: { width: '48px', height: '48px' },
  },
  title: { margin: 0, fontSize: '20px', fontWeight: 800, [theme.breakpoints.down(520)]: { fontSize: '17px' } },
  subtitle: {
    margin: '4px 0 0',
    fontSize: '13.5px',
    opacity: 0.92,
    lineHeight: 1.4,
    [theme.breakpoints.down(520)]: { fontSize: '12.5px' },
    '@media (max-height: 480px)': { fontSize: '12px', lineHeight: 1.3 },
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: '16px 20px 12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px 16px',
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
    [theme.breakpoints.down(520)]: { gridTemplateColumns: '1fr', padding: '14px 16px 12px', gap: '12px' },
  },
  item: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  icon: {
    width: '32px',
    height: '32px',
    borderRadius: '10px',
    background: '#F0ECFF',
    color: '#5B3FE0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  adminIcon: { background: '#FFF1D6', color: '#8A5A00' },
  itemTitle: { margin: 0, fontSize: '13.5px', fontWeight: 700, color: '#1a1a1a' },
  itemDesc: { margin: '2px 0 0', fontSize: '12.5px', color: '#5B6475', lineHeight: 1.4 },
  example: { fontStyle: 'italic' as const, color: '#4B33C9' },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    padding: '14px 20px 20px',
    flexShrink: 0,
    borderTop: '1px solid #EEF0F5',
    background: '#fff',
    [theme.breakpoints.down(520)]: {
      flexDirection: 'column-reverse',
      padding: '12px 16px calc(14px + env(safe-area-inset-bottom))',
      gap: '6px',
      '& button': { width: '100%' },
    },
  },
  later: {
    border: 'none',
    background: 'transparent',
    color: '#5B6475',
    fontWeight: 600,
    fontSize: '14px',
    padding: '10px 14px',
    borderRadius: '10px',
    cursor: 'pointer',
    '&:hover': { background: '#F3F4F8' },
  },
  tryIt: {
    border: 'none',
    background: 'linear-gradient(135deg, #0B64D1 0%, #6C4BF4 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: '14px',
    padding: '10px 18px',
    borderRadius: '12px',
    [theme.breakpoints.down(520)]: { padding: '13px 18px', fontSize: '15px' },
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(76, 70, 230, 0.3)',
  },
}));

const Svg: React.FC<{ d: string }> = ({ d }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

/** Catalog keys; resolved at render so the list follows the language. */
const FEATURES = [
  { key: 'book', d: 'M4 7h16M4 7v12h16V7M8 3v4M16 3v4M8 12h3' },
  { key: 'week', d: 'M4 7h16M4 7v12h16V7M8 3v4M16 3v4M9 14l2 2 4-4' },
  { key: 'waitlist', d: 'M12 8v4l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z' },
  { key: 'qr', d: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 18h2v2h-2zM14 18h2' },
  { key: 'leftovers', d: 'M4 13h16a8 8 0 0 1-16 0zM12 3v4M8 5l1 3M16 5l-1 3' },
  { key: 'seat', d: 'M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4M4 10h16v4H4zM6 14v6M18 14v6' },
  { key: 'attendance', d: 'M3 3v18h18M7 15l4-4 3 3 5-6' },
  { key: 'language', d: 'M4 5h8M8 3v2M6 11c2-1 4-4 4-6M5 9c1 2 3 3 5 3M13 21l4-9 4 9M14.5 18h5' },
];

interface IProps {
  open: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onTry: () => void;
}

/**
 * Shown once to everybody to introduce the chat: where it is, what it can do,
 * and a button that opens it.
 */
const AssistantIntroModal: React.FC<IProps> = ({ open, isAdmin, onClose, onTry }) => {
  const classes = useStyles();
  const t = useT();
  const phone = useMediaQuery('(max-width:520px)');
  const close = () => {
    markIntroSeen();
    onClose();
  };
  const tryIt = () => {
    markIntroSeen();
    onTry();
  };
  return (
    <Dialog open={open} onClose={close} classes={{ paper: classes.paper }} aria-labelledby="assistant-intro-title">
      <div className={classes.hero}>
        <span className={classes.heroRobot}>
          <RobotFace size={phone ? 38 : 50} mood="happy" />
        </span>
        <div>
          <h2 id="assistant-intro-title" className={classes.title}>
            {t('assistantIntro.title')}
          </h2>
          <p className={classes.subtitle}>{t('assistantIntro.subtitle')}</p>
        </div>
      </div>
      <ul className={classes.list}>
        {FEATURES.map((f) => (
          <li key={f.key} className={classes.item}>
            <span className={classes.icon}>
              <Svg d={f.d} />
            </span>
            <div>
              <p className={classes.itemTitle}>{t(`assistantIntro.${f.key}.title`)}</p>
              <p className={classes.itemDesc}>
                {t(`assistantIntro.${f.key}.desc`)}{' '}
                <span className={classes.example}>{t(`assistantIntro.${f.key}.example`)}</span>
              </p>
            </div>
          </li>
        ))}
        {isAdmin && (
          <li className={classes.item}>
            <span className={`${classes.icon} ${classes.adminIcon}`}>
              <Svg d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
            </span>
            <div>
              <p className={classes.itemTitle}>{t('assistantIntro.admin.title')}</p>
              <p className={classes.itemDesc}>
                {t('assistantIntro.admin.desc')}{' '}
                <span className={classes.example}>{t('assistantIntro.admin.example')}</span>
              </p>
            </div>
          </li>
        )}
      </ul>
      <div className={classes.actions}>
        <button type="button" className={classes.later} onClick={close}>
          {t('assistantIntro.later')}
        </button>
        <button type="button" className={classes.tryIt} onClick={tryIt}>
          {t('assistantIntro.try')}
        </button>
      </div>
    </Dialog>
  );
};

export default AssistantIntroModal;
